import {
  Purchases,
  type PurchasesPackage as NativePurchasePackage,
} from "@revenuecat/purchases-capacitor";
import type {
  PurchaseCustomerInfo,
  PurchasePackage,
  Result,
} from "@/types";

export type RevenueCatError =
  | "purchaseCancelled"
  | "purchaseFailed"
  | "purchaseUnavailable";

export type RevenueCatResult<Value> = Result<Value, RevenueCatError>;

export type RevenueCatClientDependencies = {
  purchases?: typeof Purchases;
};

const _customerInfo = (value: {
  activeSubscriptions: string[];
}): PurchaseCustomerInfo => ({
  activeSubscriptions: [...value.activeSubscriptions],
});

// Android reports closing the Play sheet only through the error code
// (PURCHASES_ERROR_CODE.PURCHASE_CANCELLED_ERROR); `userCancelled` is the
// deprecated flag that only iOS still sets.
const _isUserCancel = (error: unknown): boolean =>
  Boolean(error) &&
  typeof error === "object" &&
  ((error as { code?: unknown }).code === "1" ||
    (error as { userCancelled?: unknown }).userCancelled === true);

const _package = (value: NativePurchasePackage): PurchasePackage => ({
  identifier: value.identifier,
  product: {
    price: value.product.price,
    priceString: value.product.priceString,
    pricePerWeek: value.product.pricePerWeek ?? undefined,
    pricePerWeekString: value.product.pricePerWeekString ?? undefined,
  },
});

export const createRevenueCatClient = (
  dependencies: RevenueCatClientDependencies = {}
) => {
  const purchases = dependencies.purchases || Purchases;
  const nativePackages = new Map<string, NativePurchasePackage>();

  return {
    configure: async (input: { appUserId?: string; apiKey: string }) => {
      await purchases.configure({
        appUserID: input.appUserId,
        apiKey: input.apiKey,
      });
    },

    identify: async (input: {
      appUserId: string;
      email?: string;
      attributes?: Record<string, string>;
    }) => {
      await purchases.logIn({ appUserID: input.appUserId });
      if (input.email) await purchases.setEmail({ email: input.email });
      if (input.attributes && Object.keys(input.attributes).length > 0) {
        await purchases.setAttributes(input.attributes);
      }
    },

    load: async (): Promise<{
      packages: PurchasePackage[];
      customerInfo: PurchaseCustomerInfo;
    }> => {
      const [customer, offerings] = await Promise.all([
        purchases.getCustomerInfo(),
        purchases.getOfferings(),
      ]);
      const available = offerings.current?.availablePackages?.slice(0, 3) || [];
      nativePackages.clear();
      for (const item of available) nativePackages.set(item.identifier, item);
      return {
        packages: available.map(_package),
        customerInfo: _customerInfo(customer.customerInfo),
      };
    },

    status: async (): Promise<boolean> => {
      const customer = await purchases.getCustomerInfo();
      return customer.customerInfo.activeSubscriptions.length > 0;
    },

    purchase: async (
      packageIdentifier: string,
      options?: { upgradeFromProductIdentifier?: string }
    ): Promise<RevenueCatResult<PurchaseCustomerInfo>> => {
      const nativePackage = nativePackages.get(packageIdentifier);
      if (!nativePackage) return { ok: false, error: "purchaseUnavailable" };
      try {
        const purchased = await purchases.purchasePackage({
          aPackage: nativePackage,
          // Google Play only (ignored on iOS, where the App Store handles
          // in-group plan changes): without this an Android "upgrade" buys a
          // second, parallel subscription instead of replacing the old one.
          googleProductChangeInfo: options?.upgradeFromProductIdentifier
            ? {
                oldProductIdentifier:
                  options.upgradeFromProductIdentifier.split(":")[0],
              }
            : undefined,
        });
        return {
          ok: true,
          value: _customerInfo(purchased.customerInfo),
        };
      } catch (error) {
        return {
          ok: false,
          error: _isUserCancel(error) ? "purchaseCancelled" : "purchaseFailed",
        };
      }
    },

    restore: async (): Promise<RevenueCatResult<PurchaseCustomerInfo>> => {
      try {
        const restored = await purchases.restorePurchases();
        return {
          ok: true,
          value: _customerInfo(restored.customerInfo),
        };
      } catch {
        return { ok: false, error: "purchaseFailed" };
      }
    },

    setFirebaseAppInstanceId: async (firebaseAppInstanceID: string) => {
      await purchases.setFirebaseAppInstanceID({ firebaseAppInstanceID });
    },

    enableAdServicesAttribution: async () => {
      await purchases.enableAdServicesAttributionTokenCollection();
    },
  };
};

export type RevenueCatClient = ReturnType<typeof createRevenueCatClient>;
